import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import FeedResults from '../feed_builder/FeedResults';
import { useGlobal } from '../contexts/GlobalContext';
import { useBuild } from '../contexts/BuildContext';
import { StudioV1 as Studio, StudioConfig } from 'algo-dsl';

function FeedAlgoTest({ title = 'Test your feed algo', algorithm, sdkVersion, feedType, feedInputs, signature, validateInProgress }) {
  const { apiKey, baseServiceUrl, defaultFarcasterUserId, defaultPolymarketWalletAddress } = useGlobal();
  const { feedBuilderApi } = useBuild();

  const [logs, setLogs] = useState([]);
  const [results, setResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [farcasterFid, setFarcasterFid] = useState(() => defaultFarcasterUserId ?? '');
  const [polymarketWallet, setPolymarketWallet] = useState(() => defaultPolymarketWalletAddress ?? '');
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (defaultFarcasterUserId != null) setFarcasterFid((prev) => (prev === '' ? defaultFarcasterUserId : prev));
    if (defaultPolymarketWalletAddress != null) setPolymarketWallet((prev) => (prev === '' ? defaultPolymarketWalletAddress : prev));
  }, [defaultFarcasterUserId, defaultPolymarketWalletAddress]);

  const clearResults = useCallback(() => setResults(null), []);
  const clearLogs = useCallback(() => setLogs([]), []);
  const appendLog = useCallback((text) => {
    setLogs((prev) => [...prev, String(text)]);
  }, []);

  const handleRun = useCallback(() => {
    clearResults();
    clearLogs();
    if (!algorithm?.trim()) {
      appendLog('No code to run.');
      return;
    }
    const config = new StudioConfig({
      apiKey,
      commonUrl: baseServiceUrl,
      log: appendLog,
      show: setResults,
    });
    const studio = new Studio({ config });
    const search = studio.search();
    const root = feedType === 'minifeed' ? search : studio;
    setIsRunning(true);
    const done = () => setIsRunning(false);
    try {
      const AsyncFunction = (async () => {}).constructor;
      const rootParam = feedType === 'minifeed' ? 'search' : 'mbd';
      const params = feedInputs === 'user_id'
        ? `${rootParam}, farcasterFid, polymarketWallet`
        : rootParam;
      const run = new AsyncFunction(params, algorithm);
      const result = feedInputs === 'user_id'
        ? run(root, farcasterFid, polymarketWallet)
        : run(root);
      const promise = result?.then ? result : Promise.resolve(result);
      promise.catch((err) => appendLog(err?.message ?? String(err))).finally(done);
    } catch (err) {
      appendLog(err?.message ?? String(err));
      done();
    }
  }, [apiKey, baseServiceUrl, clearResults, clearLogs, appendLog, algorithm, feedType, feedInputs, farcasterFid, polymarketWallet]);

  const handleRunBackend = useCallback(async () => {
    if (!algorithm?.trim() || !signature) {
      if (!algorithm?.trim()) appendLog('No code to run.');
      return;
    }
    clearResults();
    clearLogs();
    setIsRunning(true);
    try {
      const runRes = await feedBuilderApi.runFeed({
        sdk_version: sdkVersion,
        feed_type: feedType,
        feed_inputs: feedInputs,
        farcasterFid: feedInputs === 'user_id' ? (farcasterFid || null) : null,
        polymarketWallet: feedInputs === 'user_id' ? (polymarketWallet || null) : null,
        algorithm: algorithm.trim(),
        signature,
        return_log: true,
      });
      const runResult = runRes?.result ?? runRes;
      if (runResult?.logs?.length) {
        const logs = runResult.logs || [];
        logs.push(`took_frontend: ${runRes?.took_frontend}ms`);
        setLogs(logs);
      }
      if (runResult?.feed) setResults(runResult.feed);
    } catch (err) {
      appendLog(err?.message ?? String(err));
    } finally {
      setIsRunning(false);
    }
  }, [feedBuilderApi, algorithm, sdkVersion, feedType, feedInputs, farcasterFid, polymarketWallet, signature, clearResults, clearLogs, appendLog]);

  const busy = validateInProgress || isRunning;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          {title}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {feedInputs !== 'none' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  size="small"
                  label="Farcaster User Id"
                  value={farcasterFid}
                  onChange={(e) => setFarcasterFid(e.target.value)}
                  placeholder="Farcaster FID"
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Polymarket Wallet"
                  value={polymarketWallet}
                  onChange={(e) => setPolymarketWallet(e.target.value)}
                  placeholder="0x…"
                  fullWidth
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRun}
                disabled={busy}
              >
                Run in frontend
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRunBackend}
                disabled={busy || !signature}
              >
                Run in backend
              </Button>
              {isRunning && <CircularProgress size={24} sx={{ ml: 'auto' }} />}
            </Box>
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Results" />
            <Tab label="Logs" />
          </Tabs>
          <Box sx={{ flex: 1, minHeight: 120, p: 2, overflow: 'auto', bgcolor: 'background.paper' }}>
            {tab === 0 && <FeedResults data={results} />}
            {tab === 1 && (
              <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {logs.length === 0 ? 'No logs yet.' : logs.join('\n')}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default FeedAlgoTest;
